import React, { FC, PropsWithChildren, createContext, useContext, useState } from 'react';

import { JourneyReviewTabs } from 'modules/workflows/components/JourneyReview/JourneyReview.constants';
import { getNodes, getWorkflowHasUnsavedChanges } from 'modules/workflows/ducks';
import { useSelector } from 'react-redux';
import { NodeError } from '../../hooks';

import { TileReviewItem } from '../../components/JourneyReview/JourneyReview.types';
import { useJourneyIssues } from './useJourneyIssues';
import { useJourneyPerformanceOptimizations } from './useJourneyPerformanceOptimizations';
import { useJourneyReviewRefresher } from './useJourneyReviewRefresher';

// refactor to compose this type based off of the hook return type

export type JourneyReviewContextType = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  activeTab: JourneyReviewTabs;
  setActiveTab: (tabName: JourneyReviewTabs) => void;
  openAndRunChecks: () => void;
  runChecks: () => void;
  getIssues: () => void;
  setIssues: (issues: NodeError[]) => void;
  completeIssue: (nodeId: string) => void;
  clearCompleted: () => void;
  completeOptimizations: (optimizationIds: string[], shouldRefetchJourney?: boolean) => void;
  issues: TileReviewItem[];
  hasIssuesError: boolean;
  isIssuesLoading: boolean;
  optimizations: TileReviewItem[];
  hasOptimizationError: boolean;
  isOptimizationsLoading: boolean;
  incompleteIssuesCount: number;
  incompleteOptimizationsCount: number;
  getOptimizations: () => void;
};

const JourneyReviewContextState = {
  isOpen: false,
  setIsOpen: () => {},
  activeTab: JourneyReviewTabs.Issues,
  setActiveTab: () => {},
  openAndRunChecks: () => {},
  runChecks: () => {},
  getIssues: () => {},
  setIssues: () => {},
  completeIssue: () => {},
  completeOptimizations: () => {},
  issues: [],
  hasIssuesError: false,
  isIssuesLoading: true,
  optimizations: [],
  hasOptimizationError: false,
  isOptimizationsLoading: true,
  incompleteIssuesCount: 0,
  incompleteOptimizationsCount: 0,
  getOptimizations: () => {},
  clearCompleted: () => {},
};

export const JourneyReviewContext =
  createContext<JourneyReviewContextType>(JourneyReviewContextState);

export const JourneyReviewContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}: PropsWithChildren<Record<string, any>>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(JourneyReviewTabs.Issues);
  const nodes = useSelector(getNodes);
  const hasUnsavedChanges = useSelector(getWorkflowHasUnsavedChanges);

  const {
    validateJourney,
    onOptimizationComplete,
    clearCompletedIssues,
    setCachedIssuesOnStore,
    setIssuesToCache,
    incompleteIssuesCount,

    ...restIssue
  } = useJourneyIssues({ activeTab });

  const {
    highlightOptimizations,
    hideOptimizations,
    getOptimizations,
    clearCompletedOptimizations,
    incompleteOptimizationsCount,
    ...restOptimizations
  } = useJourneyPerformanceOptimizations({
    activeTab,
    onOptimizationComplete,
  });

  const clearCompleted = () => {
    clearCompletedIssues();
    clearCompletedOptimizations();
  };

  const { refreshJourneyReview } = useJourneyReviewRefresher({
    validateJourney,
    getOptimizations,
    clearCompleted: clearCompletedIssues,
  });

  const openAndRunChecks = () => {
    refreshJourneyReview();
    setIsOpen(true);
  };

  const handleSetActiveTab = (tabName: JourneyReviewTabs) => {
    if (tabName === JourneyReviewTabs.Issues) {
      hideOptimizations();
      setCachedIssuesOnStore();
    }
    if (tabName === JourneyReviewTabs.Performance) {
      highlightOptimizations();
      setIssuesToCache();
    }
    setActiveTab(tabName);
  };

  const handleSetIsOpen = (openPanel: boolean) => {
    if (openPanel && !hasUnsavedChanges && nodes.length === 1 && incompleteIssuesCount === 0) {
      refreshJourneyReview();
    }

    if (!openPanel) {
      handleSetActiveTab(JourneyReviewTabs.Issues);
    }
    setIsOpen(openPanel);
  };

  const state = {
    ...restIssue,
    ...restOptimizations,
    isOpen,
    setIsOpen: handleSetIsOpen,
    activeTab,
    setActiveTab: handleSetActiveTab,
    openAndRunChecks,
    runChecks: refreshJourneyReview,
    getIssues: validateJourney,
    getOptimizations,
    clearCompleted,
    incompleteOptimizationsCount,
    incompleteIssuesCount,
  };

  return <JourneyReviewContext.Provider value={state}>{children}</JourneyReviewContext.Provider>;
};

export const useJourneyReviewContext = () => useContext(JourneyReviewContext);
